import { httpStatus } from '@/core/constants/httpStatus.ts'
import {
  HttpStatusCode,
  httpStatusCodeSchema
} from '@/core/types/HttpStatus.ts'
import { useCallback } from 'react'
import { toast } from 'react-toastify'
import { env } from '@/env.ts'
import { z } from 'zod'
import { validateSchema } from '@/core/utils/safeSchemaValidations.ts'
import { FieldValues, UseFormSetError } from 'react-hook-form'
import { isFormValidationError } from '@/core/validations/validations.ts'
import { Exceptionless } from '@exceptionless/react'
import { removeStoredTokens } from '@/core/utils/security.ts'
import { goToLoginPage } from '@/core/utils/goToPages.ts'
import loginService from '@/modules/security/login/services/loginService.ts'

export const isApiGeneralError = (err: unknown): err is ApiGeneralError =>
  !!err && typeof err === 'object' && 'message' in err

export const isAPICustomError = (err: unknown): err is APICustomError =>
  !!err &&
  typeof err === 'object' &&
  'message' in err &&
  typeof (err as any).message === 'string' &&
  'data' in err &&
  typeof (err as any).data === 'object'

export const isTAPIError = (err: unknown): err is TAPIError =>
  !!err &&
  typeof err === 'object' &&
  'code' in err &&
  'status' in err &&
  'requestUrl' in err &&
  'details' in err

export type TAPIError = {
  code?: string
  status?: HttpStatusCode
  requestUrl?: string
  details: ApiGeneralError | APICustomError
}

export const apiGeneralErrorSchema = z.object({
  code: httpStatusCodeSchema,
  message: z.string()
})
export type ApiGeneralError = z.infer<typeof apiGeneralErrorSchema>

export const apiCustomErrorSchema = z.object({
  message: z.string(),
  data: z.object({
    error: z.string()
  })
})
export type APICustomError = z.infer<typeof apiCustomErrorSchema>

const generalErrorText =
  'Ha ocurrido un error en el sistema. Por favor contacte a su administrador.'

const useHandleAPIError = () => {
  const showError = useCallback(
    (description: string) => toast.error(description),
    []
  )

  const showGenericError = useCallback(
    (context?: string) => showError(context || generalErrorText),
    [showError]
  )

  const handleNonCustomerErrors = useCallback(
    (error: TAPIError, context?: string) => {
      switch (error.status) {
        case httpStatus.NOT_AUTHORIZED: {
          if (isAPICustomError(error)) {
            showError(error.message || context || generalErrorText)
          } else showError(context || 'El sistema no reconoce sus credenciales')
          break
        }
        case httpStatus.SERVER_ERROR: {
          if (isAPICustomError(error)) {
            showError(error.message || context || generalErrorText)
          } else showError(context || generalErrorText)
          break
        }
        case httpStatus.BAD_REQUEST: {
          if (isAPICustomError(error)) {
            showError(error.message || context || generalErrorText)
          } else showError(context || 'La acción es inválida')
          break
        }
        case httpStatus.CONFLICT: {
          if (isAPICustomError(error)) {
            showError(error.message || context || generalErrorText)
          } else
            showError(
              context ||
                'Por favor contacte a su administrador. El servidor ha mandado un error de conflicto.'
            )
          break
        }
        default: {
          if (isAPICustomError(error)) {
            showError(error.message || context || generalErrorText)
          } else showGenericError(context)
        }
      }
    },
    [showError, showGenericError]
  )

  const handleCustomerErrors = useCallback(
    <T extends FieldValues>(
      customError: ApiGeneralError | APICustomError,
      context?: string,
      setError?: UseFormSetError<T>
    ) => {
      if (setError && isFormValidationError(customError)) {
        const backendErrors = customError.data.error

        Object.entries(backendErrors).forEach(([field, messages]) => {
          setError!(field as any, {
            type: 'server',
            message:
              (Array.isArray(messages) ? messages[0] : messages) ||
              'Error en este campo'
          })
        })
      }
      if (isAPICustomError(customError)) {
        validateSchema(apiCustomErrorSchema, customError)
        showError(customError.message || context || generalErrorText)
      } else if (isApiGeneralError(customError)) {
        validateSchema(apiGeneralErrorSchema, customError)
        showError(context || generalErrorText)
      } else {
        showError(context || generalErrorText)
      }
    },
    [showError]
  )

  const handleAPIError = useCallback(
    <T extends FieldValues>(
      error: unknown,
      context?: string,
      setError?: UseFormSetError<T>
    ) => {
      if (!env.PROD) {
        console.error('WEPPA API error ', error)
      } else {
        void Exceptionless.submitException(error as Error)
      }
      if (!isTAPIError(error)) showError('Error desconocido')
      else if (error?.code === 'ERR_NETWORK') {
        removeStoredTokens()
        goToLoginPage()
        void loginService.logout()
      } else if (error.status && (error.status < 400 || error.status >= 500)) {
        handleNonCustomerErrors(error, context)
      } else if (error.status && (error.status >= 400 || error.status < 500)) {
        handleCustomerErrors(error.details, context, setError)
      }
    },
    [showError, handleNonCustomerErrors, handleCustomerErrors]
  )

  return {
    handleAPIError
  }
}

export default useHandleAPIError
